import { useParams } from "react-router-dom";
import { SpecTable } from "@components/SpecTable";
import { ProsCons } from "@components/ProsCons";
import { ReviewForm } from "@components/ReviewForm";
import { ReviewsList } from "@components/ReviewsList";
import { useProductDetail, useReviews } from "@lib/hooks";

export function ProductPage() {
  const { slug } = useParams();
  const { data, isLoading, isError } = useProductDetail(slug ?? "");
  const productId = data?.product.id ?? "";
  const reviewsQuery = useReviews(productId);

  if (isLoading) {
    return <p>Loading product…</p>;
  }

  if (isError || !data) {
    return <p>Unable to load this product.</p>;
  }

  const { product, canonical, citations } = data;

  return (
    <div className="page product">
      <header>
        <h1>{product.name}</h1>
        <p>
          {product.brand} • {product.category}
        </p>
        <p className="summary">{canonical.summary}</p>
      </header>
      <section>
        <h2>Specifications</h2>
        <SpecTable specs={canonical.specs} />
      </section>
      <section>
        <ProsCons pros={canonical.pros} cons={canonical.cons} />
      </section>
      <section className="best-for">
        <h3>Best For</h3>
        {canonical.best_for.length ? (
          <ul>{canonical.best_for.map((item) => <li key={item}>{item}</li>)}</ul>
        ) : (
          <p>We are still evaluating the best use cases.</p>
        )}
      </section>
      <section className="gotchas">
        <h3>Gotchas</h3>
        {canonical.gotchas.length ? (
          <ul>{canonical.gotchas.map((item) => <li key={item}>{item}</li>)}</ul>
        ) : (
          <p>No notable drawbacks yet.</p>
        )}
      </section>
      <section className="faq">
        <h3>FAQ</h3>
        {canonical.faq.length ? (
          <dl>
            {canonical.faq.map((entry) => (
              <div key={entry.q}>
                <dt>{entry.q}</dt>
                <dd>{entry.a}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p>No frequently asked questions yet.</p>
        )}
      </section>
      <section className="media">
        <h3>Media</h3>
        <div className="media-grid">
          {canonical.media.images.map((image) => (
            <img key={image} src={image} alt={product.name} />
          ))}
          {canonical.media.videos.map((video) => (
            <iframe
              key={video.youtubeId}
              src={`https://www.youtube.com/embed/${video.youtubeId}`}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ))}
        </div>
      </section>
      <section className="citations">
        <h3>Sources</h3>
        {citations.length ? (
          <ul>
            {citations.map((citation: any) => (
              <li key={citation.id ?? citation.url}>
                <a href={citation.url} target="_blank" rel="noopener noreferrer">
                  {citation.title || citation.url}
                </a>{" "}
                <span className="badge">{citation.source_type}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p>No citations available yet.</p>
        )}
      </section>
      <section className="reviews">
        {reviewsQuery.data ? (
          <ReviewsList reviews={reviewsQuery.data.reviews} aggregate={reviewsQuery.data.aggregate} />
        ) : null}
        <ReviewForm productId={product.id} />
      </section>
    </div>
  );
}
